// Shop.jsx — финальная версия с корректной работой для всех категорий
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import API from '../api/MainApi';
import ShopHeader from '../components/ShopHeader';
import Filters from '../components/Filters';
import VerticalCatalog from '../components/VerticalCatalog';
import HorizontalCatalog from '../components/HorizontalCatalog';
import WishlistModal from '../components/WishlistModal';
import ImageViewer from '../components/ImageViewer';
import { useWishlistContext } from '../context/WishlistContext';
import useMediaQuery from '../hooks/useMediaQuery';
import '../styles/Shop.css';
import likeIcon from '../icons/like.svg';
import nazad from '../icons/nazad.svg';
import posteriBotom from '../images/posteri.png';
import kubikiBotom from '../images/cubiki.png';

const Shop = ({ type: category }) => {
    const { id: productIdFromUrl } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const [selectedFilters, setSelectedFilters] = useState({
        type: 'all',
        sort: 'newest',
        category: 'toys' // для архива
    });
    const [page, setPage] = useState(1);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [showWishlist, setShowWishlist] = useState(false);
    const loadingRef = useRef(false);

    const isMobile = useMediaQuery('(max-width: 767px)');
    const { products: wishlistItems, loading: wishlistLoading, removeItem, total } = useWishlistContext();

    const goBack = () => navigate('/');

    const getApiParams = useCallback(() => {
        const params = {
            type: selectedFilters.type,
            sort: selectedFilters.sort,
        };
        if (category === 'toys') {
            params.category = 'Doodle Toys';
        } else if (category === 'cards') {
            params.category = 'Открытки';
        } else if (category === 'posters') {
            params.categories = ['Постеры'];
        } else if (category === 'cubes') {
            params.categories = ['Кубики', 'Магниты'];
        } else if (location.pathname === '/archive') {
            params.category = 'Doodle Toys';
            

        }
        return params;
    }, [category, selectedFilters, location.pathname]);

    const loadLimit = (category === 'posters' || category === 'cubes') ? 12 : 6;

    const loadProducts = useCallback(async (pageNum, replace) => {
        if (loadingRef.current) return;
        loadingRef.current = true;
        setLoading(true);
        try {
            const apiParams = getApiParams();
            const res = await API.getProducts({
                ...apiParams,
                page: pageNum,
                limit: loadLimit,
                isArch: location.pathname === '/archive'
            });
            const newProducts = res.data;
            setProducts(prev => replace ? newProducts : [...prev, ...newProducts]);
            setHasMore(pageNum < res.pagination.pages);
            setPage(pageNum);
        } catch (error) {
            console.error('Ошибка загрузки товаров:', error);
        } finally {
            loadingRef.current = false;
            setLoading(false);
        }
    }, [getApiParams, loadLimit]);

    useEffect(() => {
        setPage(1);
        setProducts([]);
        setHasMore(true);
        loadProducts(1, true);
    }, [category, selectedFilters]);

    const loadMore = useCallback(() => {
        if (hasMore && !loadingRef.current) {
            loadProducts(page + 1, false);
        }
    }, [hasMore, page, loadProducts]);

    // Редирект с /toys/:id на хеш (без перезагрузки)
    useEffect(() => {
        if (productIdFromUrl && category === 'toys') {
            navigate(`/toys#product/${productIdFromUrl}`, { replace: true });
        }
    }, [productIdFromUrl, category, navigate]);

    // Синхронизация хеша и модалки только для игрушек
    useEffect(() => {
        if (category !== 'toys') return;

        const syncFromHash = () => {
            const hash = window.location.hash;
            if (hash.startsWith('#product/')) {
                const id = hash.replace('#product/', '');
                if (!selectedProduct || selectedProduct._id !== id) {
                    API.getProductById(id)
                        .then(res => {
                            if (res.data.category === 'Doodle Toys') {
                                setSelectedProduct(res.data);
                            }
                        })
                        .catch(err => console.error('Товар не найден', err));
                }
            } else {
                if (selectedProduct) setSelectedProduct(null);
            }
        };

        syncFromHash();
        window.addEventListener('hashchange', syncFromHash);
        return () => window.removeEventListener('hashchange', syncFromHash);
    }, [category, selectedProduct]);

    const handleCardClick = useCallback((product) => {
        if (category === 'toys') {
            window.location.hash = `product/${product._id}`;
        } else {
            setSelectedProduct(product);
        }
    }, [category]);

    const handleCloseViewer = useCallback(() => {
        if (category === 'toys') {
            setSelectedProduct(null);
            window.history.replaceState(null, '', window.location.pathname + window.location.search);
        } else {
            setSelectedProduct(null);
        }
    }, [category]);

    const useHorizontal = (category === 'posters' || category === 'cubes') && !isMobile;
    const itemsPerSlide = category === 'posters' ? 2 : 3;

    return (
        <section className="ShopContainer">
            <ShopHeader type={category} />
            <div className={`shopMain ${category === 'posters' || category === 'cubes' ? 'noBigPad' : ''}`}>
                {(category === 'toys' || category === 'cards' || location.pathname == '/archive') && (
                    <Filters
                        isArch={location.pathname == '/archive'}
                        type={category}
                        selectedFilters={selectedFilters}
                        setSelectedFilters={setSelectedFilters}
                    />
                )}
                {useHorizontal ? (
                    <div className='shopHorWork'>
                        <HorizontalCatalog
                            products={products}
                            loading={loading}
                            hasMore={hasMore}
                            onLoadMore={loadMore}
                            onCardClick={handleCardClick}
                            itemsPerSlide={itemsPerSlide}
                        />
                        <img src={nazad} alt="Назад" className='shopGoBackBotom' onClick={goBack} />
                        {category === 'posters' && <img src={posteriBotom} alt="Постеры" className='shopPosteriBottomText' />}
                        {category === 'cubes' && <img src={kubikiBotom} alt="Кубики" className='shopPosteriBottomText' />}
                    </div>
                ) : (
                    <>
                        <VerticalCatalog
                            products={products}
                            loading={loading}
                            hasMore={hasMore}
                            onLoadMore={loadMore}
                            onCardClick={handleCardClick}
                        />
                        {category === 'posters' && <img src={posteriBotom} alt="Постеры" className='shopPosteriBottomText' />}
                        {category === 'cubes' && <img src={kubikiBotom} alt="Кубики" className='shopPosteriBottomText' />}
                    </>
                )}
            </div>

            <button className="wishlistFloatingButton" onClick={() => setShowWishlist(true)}>
                <img src={likeIcon} alt="Избранное" />
                {wishlistItems.length > 0 && <span className="wishlistCount">{wishlistItems.length}</span>}
            </button>

            {showWishlist && (
                <WishlistModal
                    items={wishlistItems}
                    loading={wishlistLoading}
                    total={total}
                    onClose={() => setShowWishlist(false)}
                    onRemove={removeItem}
                />
            )}
            {selectedProduct && (
                <ImageViewer product={selectedProduct} onClose={handleCloseViewer} />
            )}
        </section>
    );
};

export default Shop;